import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import { useSearchMySubordinateParticipants } from "@/queries/employees/use-employee-performance-review.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiPerformanceReviewParticipant,
  ApiPerformanceReviewParticipantFilter,
} from "@/types/performance-review.types.ts";
import {
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  Page,
  PaginationBlock,
  Stack,
  Table,
  TableCell,
  TextTruncated,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";
import { useSearchParams } from "react-router-dom";

export const MyReviewsTeam = () => {
  const { t } = useTranslation();
  const { getPerformanceReviewStatusTag } = usePerformanceReviewsHook();
  const { renderDate, renderTimeAgo } = useFormatter();
  const library = filtersLibrary();
  const [, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const {
    filter: { filter: filter },
  } = useAppFilter<ApiPerformanceReviewParticipantFilter>({
    filters: [library.page_number, library.text],
    source: "My reviews team",
  });

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set(library.text.searchParamName, searchTerm);
        else searchParams.delete(library.text.searchParamName);
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const {
    data: participantData,
    isLoading: isLoading,
    isFetching: isFetching,
  } = useSearchMySubordinateParticipants(filter);

  const subordinateColumns = useMemo<ColumnDef<ApiPerformanceReviewParticipant>[]>(() => {
    return [
      {
        id: "employees",
        header: t("core:full_name"),
        cell: (cell) => (
          <EmployeeHoverCard
            employee={cell.row.original.employee}
            position={getEmployeePositionTitle(cell.row.original.employee)}
          >
            <EmployeeItem showPosition={false} employee={cell.row.original.employee} />
          </EmployeeHoverCard>
        ),
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
      {
        id: "date",
        header: t("common:date"),
        cell: (cell) => {
          if (!cell.row.original.review.date_completed_at) return "-";
          const date = new Date(cell.row.original.review.date_completed_at);
          return (
            <Tooltip>
              <TooltipTrigger>
                <TableCell variant="secondary">{renderTimeAgo(date)}</TableCell>
              </TooltipTrigger>
              <TooltipContent>{renderDate(date)}</TooltipContent>
            </Tooltip>
          );
        },
        ...ColumnSizes.standard,
      },
      {
        id: "cycle_status",
        header: t("performance_review:cycle_status"),
        cell: (cell) => (
          <Stack items={"start"}>
            {getPerformanceReviewStatusTag(cell.row.original.review.status)}
          </Stack>
        ),
        ...ColumnSizes.standard,
      },
      {
        id: "review_cycle",
        header: t("performance_review:review_cycle"),
        cell: (cell) => (
          <TextTruncated title={cell.row.original.review.title} className={"text-secondary"} />
        ),
        ...ColumnSizes.standard,
      },
    ];
  }, [getPerformanceReviewStatusTag, renderDate, renderTimeAgo, t]);

  const renderRowAction = useCallback(
    (row: Row<ApiPerformanceReviewParticipant>) => (
      <DropdownMenu>
        <DropdownMenuItem
          title={t("common:open_results")}
          target={"_blank"}
          to={`/performance-reviews/${row.original.review.id}/view/participants/${row.original.employee.id}`}
        />
      </DropdownMenu>
    ),
    [t]
  );

  return (
    <Page.StickyLayout
      toolbar={
        <Search
          value={searchTerm}
          onInput={(e) => setSearchTerm(e.currentTarget.value)}
          placeholder={t("performance_review:colleague_name")}
        />
      }
      footer={
        <PaginationBlock
          metadata={participantData?.metadata}
          onPageChange={(page) => {
            setSearchParams((searchParams) => {
              searchParams.set("page", page.toString());
              return searchParams;
            });
          }}
        />
      }
    >
      <Table
        stickyHeader={true}
        renderRowAction={renderRowAction}
        onRowClick={(row) =>
          window.open(
            `/performance-reviews/${row.original.review.id}/view/participants/${row.original.employee.id}`,
            "_blank"
          )
        }
        data={participantData?.items || []}
        columns={subordinateColumns}
        showSkeleton={isLoading}
        isFetching={isLoading || isFetching}
        showNotFoundError={filter.text != undefined && participantData?.metadata.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={
          <EmptyStateBase
            emoji={emojiDefaults.default}
            title={t("performance_review:my_team_empty_title")}
            description={t("performance_review:my_team_empty_description")}
          />
        }
      />
    </Page.StickyLayout>
  );
};
